import { useEffect, useRef, type CSSProperties, type FocusEvent } from "react";
import { useTranslation } from "react-i18next";
import { Monitor, Pause, Play, Smartphone } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackButtonClick } from "@/utils/analytics";
import { DevicePreview } from "./DevicePreview";
import { FeatureCards, FeatureChips, type ScreenCopy } from "./FeatureNavigation";
import {
  SHOWCASE_SETTINGS,
  showcaseImage,
  type ShowcaseDevice,
  type ShowcasePage,
  type ShowcaseScreenId,
} from "./productShowcaseConfig";
import { useProductShowcase } from "./useProductShowcase";
import "./product-showcase.css";

const DEVICE_ICONS = { mobile: Smartphone, desktop: Monitor } as const;
const K = "sections.productShowcase";

/**
 * Interactive product tour: real Bidmio screens in a phone or browser frame,
 * with the screens of this page listed beside it (cards on wide screens,
 * pills underneath on phones and tablets). `page` is "home" or a module slug
 * from SHOWCASE_PAGES; each page shows its own screens and heading.
 */
export function InteractiveProductShowcase({ page, className }: { page: ShowcasePage; className?: string }) {
  const { t, i18n } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const state = useProductShowcase(page, sectionRef);
  const { device, screens, activeId, activeIndex } = state;
  const sectionId = page === "home" ? "product-showcase" : `product-showcase-${page}`;
  const panelId = `${sectionId}-screen`;

  const copy = (id: ShowcaseScreenId): ScreenCopy => ({
    title: t(`${K}.screens.${id}.title`),
    description: t(`${K}.screens.${id}.description`),
  });
  const active = copy(activeId);
  const deviceLabel = t(`${K}.devices.${device}`);
  const image = showcaseImage(i18n.language, activeId, device);

  // Fetch the next screen while this one shows, so autoplay never lands on a blank frame.
  useEffect(() => {
    if (!state.inView || typeof window === "undefined") return;
    const next = screens[(activeIndex + 1) % screens.length];
    const preload = new Image();
    const { src, srcSet, sizes } = showcaseImage(i18n.language, next, device);
    if (sizes) preload.sizes = sizes;
    if (srcSet) preload.srcset = srcSet;
    preload.src = src;
  }, [state.inView, screens, activeIndex, device, i18n.language]);

  const select = (id: ShowcaseScreenId) => {
    state.select(id);
    trackButtonClick(`showcase_${page}_${device}_${id}`, "product_showcase");
  };
  const chooseDevice = (next: ShowcaseDevice) => {
    if (next === device) return;
    state.changeDevice(next);
    trackButtonClick(`showcase_${page}_device_${next}`, "product_showcase");
  };

  const half = Math.ceil(screens.length / 2);
  const clockKey = `${device}-${activeId}`;
  const timed: CSSProperties = {
    animationDuration: `${SHOWCASE_SETTINGS.autoplayMs}ms`,
    animationPlayState: state.playing ? "running" : "paused",
  };
  const navFocus = {
    // Focus anywhere in the section pauses autoplay, except on the play button itself.
    onFocus: (event: FocusEvent<HTMLElement>) =>
      state.setFocused(!(event.target as HTMLElement).closest(".ps-toggle")),
    onBlur: (event: FocusEvent<HTMLElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null)) state.setFocused(false);
    },
  };
  const cardProps = {
    activeId,
    copy,
    panelId,
    onSelect: select,
    autoplay: state.autoplay,
    playing: state.playing,
    clockKey,
    clockMs: SHOWCASE_SETTINGS.autoplayMs,
  };

  return (
    <section
      ref={sectionRef}
      id={sectionId}
      aria-labelledby={`${sectionId}-title`}
      className={cn("ps-section section-padding", className)}
      style={{ "--ps-ms": `${SHOWCASE_SETTINGS.transitionMs}ms` } as CSSProperties}
      data-testid="product-showcase"
      data-ps-root
      {...navFocus}
    >
      <div className="container-custom">
        <header className="ps-head">
          <p className="ps-eyebrow">{t(`${K}.pages.${page}.eyebrow`)}</p>
          <h2 id={`${sectionId}-title`} className="ps-title">
            {t(`${K}.pages.${page}.title`)}
          </h2>
          <p className="ps-subtitle">{t(`${K}.pages.${page}.subtitle`)}</p>
        </header>

        {state.devices.length > 1 ? (
          <div className="ps-switch-wrap">
            <div className="ps-switch" role="group" aria-label={t(`${K}.deviceSwitch`)} data-device={device}>
              <span className="ps-switch-pill" aria-hidden="true" />
              {state.devices.map((option) => {
                const Icon = DEVICE_ICONS[option];
                return (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={option === device}
                    onClick={() => chooseDevice(option)}
                    data-testid={`product-showcase-device-${option}-button`}
                  >
                    <Icon aria-hidden="true" />
                    <span className="ps-switch-long">{t(`${K}.devices.${option}`)}</span>
                    <span className="ps-switch-short">{t(`${K}.devicesShort.${option}`)}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div
          className="ps-layout"
          data-device={device}
          onPointerEnter={(event) => event.pointerType === "mouse" && state.setHovered(true)}
          onPointerLeave={() => state.setHovered(false)}
        >
          <div className="ps-col">
            <FeatureCards {...cardProps} screens={screens.slice(0, half)} label={t(`${K}.navLabel`)} />
          </div>

          <div className="ps-center">
            <div className="ps-device-wrap">
              <DevicePreview
                device={device}
                index={activeIndex}
                panelId={panelId}
                panelLabel={`${deviceLabel}: ${active.title}`}
                urlLabel={t(`${K}.appUrl`)}
                screen={{
                  id: activeId,
                  ...image,
                  alt: t(`${K}.screenAlt`, { device: deviceLabel, screen: active.title, description: active.description }),
                }}
              />
            </div>

            <div className="ps-under">
              <span className="ps-real">{t(`${K}.realNote`)}</span>
              {SHOWCASE_SETTINGS.autoplay && !state.reducedMotion && state.canCycle ? (
                <button type="button" className="ps-toggle" onClick={state.toggleAutoplay}>
                  {state.autoplay ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
                  {state.autoplay ? t(`${K}.autoplay.pause`) : t(`${K}.autoplay.play`)}
                </button>
              ) : null}
              {state.autoplay ? (
                <span className="ps-clock-track" aria-hidden="true">
                  <span key={clockKey} className="ps-clock" style={timed} onAnimationEnd={state.tick} />
                </span>
              ) : null}
            </div>

            <div className="ps-mobile-nav">
              <FeatureChips screens={screens} activeId={activeId} copy={copy} panelId={panelId} onSelect={select} label={t(`${K}.navLabel`)} />
              <div className="ps-active">
                <p className="ps-active-title">{active.title}</p>
                <p className="ps-active-desc">{active.description}</p>
              </div>
            </div>
          </div>

          <div className="ps-col">
            <FeatureCards {...cardProps} screens={screens.slice(half)} label={t(`${K}.navLabelMore`)} />
          </div>
        </div>
      </div>
    </section>
  );
}

export default InteractiveProductShowcase;
